import styled from 'styled-components';
import { Link } from 'react-router-dom';

/* ── Hero Carousel ────────────────────────────────────── */

export const SlideWrapper = styled.div`
  background: ${({ $bgColor }) => $bgColor || '#FFF5F4'};
  min-height: 400px;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 60px 40px;
`;

export const SlideContent = styled.div`
  max-width: 700px;
  text-align: center;
`;

export const SlideTitle = styled.h1`
  font-family: ${({ theme }) => theme.typography.heading};
  font-size: ${({ theme }) => theme.typography.sizes.hero};
  color: ${({ $textColor }) => $textColor || '#333333'};
  margin-bottom: 16px;
  line-height: 1.2;
`;

export const SlideSubtitle = styled.p`
  font-family: ${({ theme }) => theme.typography.body};
  font-size: ${({ theme }) => theme.typography.sizes.lg};
  color: ${({ $textColor }) => $textColor || '#333333'};
  margin-bottom: 32px;
  opacity: 0.85;
`;

export const SlideCTA = styled(Link)`
  display: inline-block;
  background: ${({ theme }) => theme.colors.secondary.main};
  color: ${({ theme }) => theme.colors.primary.dark};
  padding: 12px 32px;
  border-radius: ${({ theme }) => theme.metrics.radius.medium};
  font-weight: 700;
  font-size: ${({ theme }) => theme.typography.sizes.md};
  transition: background 0.2s;

  &:hover {
    background: ${({ theme }) => theme.colors.secondary.dark};
    color: ${({ theme }) => theme.colors.primary.dark};
  }
`;

/* ── Scrollable Row (Popular Books) ───────────────────── */

export const ScrollRow = styled.div`
  display: flex;
  gap: 20px;
  overflow-x: auto;
  padding-bottom: 12px;

  /* Custom scrollbar */
  &::-webkit-scrollbar {
    height: 6px;
  }
  &::-webkit-scrollbar-track {
    background: ${({ theme }) => theme.colors.bgSecondary};
    border-radius: 3px;
  }
  &::-webkit-scrollbar-thumb {
    background: ${({ theme }) => theme.colors.border};
    border-radius: 3px;
  }
`;

export const BookCardWrapper = styled.div`
  flex: 0 0 220px;
  min-width: 220px;
`;

/* ── Best Sellers Grid ────────────────────────────────── */

export const BooksGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 20px;

  @media (max-width: 1024px) {
    grid-template-columns: repeat(3, 1fr);
  }

  @media (max-width: 768px) {
    grid-template-columns: repeat(2, 1fr);
  }

  @media (max-width: 480px) {
    grid-template-columns: 1fr;
  }
`;

/* ── Sellers Section ──────────────────────────────────── */

export const SellersRow = styled.div`
  display: flex;
  gap: 20px;
  overflow-x: auto;
  padding-bottom: 12px;

  &::-webkit-scrollbar {
    height: 6px;
  }
  &::-webkit-scrollbar-track {
    background: ${({ theme }) => theme.colors.bgSecondary};
    border-radius: 3px;
  }
  &::-webkit-scrollbar-thumb {
    background: ${({ theme }) => theme.colors.border};
    border-radius: 3px;
  }
`;

export const SellerCard = styled(Link)`
  flex: 0 0 200px;
  min-width: 200px;
  background: ${({ theme }) => theme.colors.bgColor};
  border: 2px solid ${({ theme }) => theme.colors.border};
  border-radius: ${({ theme }) => theme.metrics.radius.medium};
  padding: 24px 16px;
  text-align: center;
  transition: all 0.3s;
  color: inherit;

  &:hover {
    box-shadow: 0 4px 16px ${({ theme }) => theme.colors.shadow};
    transform: translateY(-4px);
    border-color: ${({ theme }) => theme.colors.accent.main};
    color: inherit;
  }
`;

export const SellerName = styled.h4`
  font-family: ${({ theme }) => theme.typography.heading};
  font-size: ${({ theme }) => theme.typography.sizes.md};
  color: ${({ theme }) => theme.colors.text.dark.very};
  margin-bottom: 8px;
`;

export const SellerLocation = styled.span`
  display: block;
  font-size: ${({ theme }) => theme.typography.sizes.xs};
  color: ${({ theme }) => theme.colors.text.dark.little};
  margin-bottom: 8px;
`;

export const SellerRating = styled.span`
  display: inline-block;
  font-size: ${({ theme }) => theme.typography.sizes.sm};
  color: ${({ theme }) => theme.colors.auxiliar.warning};
  font-weight: 600;
`;

/* ── Stats Section ────────────────────────────────────── */

export const StatsSection = styled.section`
  background: ${({ theme }) => theme.colors.info.main};
  padding: 48px 24px;
  margin: 48px 0;
  border-radius: ${({ theme }) => theme.metrics.radius.large};
`;

export const StatsRow = styled.div`
  display: flex;
  justify-content: space-around;
  flex-wrap: wrap;
  gap: 24px;
`;

export const StatBox = styled.div`
  text-align: center;
  flex: 1;
  min-width: 140px;
`;

export const StatNumber = styled.div`
  font-family: ${({ theme }) => theme.typography.heading};
  font-size: ${({ theme }) => theme.typography.sizes.xxl};
  color: ${({ theme }) => theme.colors.secondary.dark};
  font-weight: 700;
  margin-bottom: 4px;
`;

export const StatLabel = styled.div`
  font-size: ${({ theme }) => theme.typography.sizes.sm};
  color: ${({ theme }) => theme.colors.text.dark.medium};
`;

/* ── Section spacing ──────────────────────────────────── */

export const Section = styled.section`
  margin-bottom: 48px;
`;

export const CarouselSection = styled.section`
  margin-bottom: 48px;

  /* Override antd carousel dots styling */
  .ant-carousel .slick-dots li button {
    background: ${({ theme }) => theme.colors.text.dark.little};
  }
  .ant-carousel .slick-dots li.slick-active button {
    background: ${({ theme }) => theme.colors.primary.main};
  }
`;
